const GRAPH = 'https://graph.microsoft.com/v1.0';
const SITE_HOST = 'fragosinsurancegroupllc.sharepoint.com';
const SITE_PATH = '/sites/DefineHolyFamily';
const LIBRARY_NAME = 'Family Vault';

let cachedDriveId = null;

async function resolveDriveId(token) {
  if (cachedDriveId) return cachedDriveId;
  const siteRes = await fetch(`${GRAPH}/sites/${SITE_HOST}:${SITE_PATH}:`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!siteRes.ok) throw new Error(`Failed to resolve site (${siteRes.status})`);
  const site = await siteRes.json();
  const drivesRes = await fetch(`${GRAPH}/sites/${site.id}/drives?$select=name,id&$top=100`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!drivesRes.ok) throw new Error(`Failed to load drives (${drivesRes.status})`);
  const drivesData = await drivesRes.json();
  const drive = (drivesData.value || []).find(d => d.name.toLowerCase() === LIBRARY_NAME.toLowerCase());
  if (!drive) throw new Error('Family Vault not found');
  cachedDriveId = drive.id;
  return drive.id;
}

export async function searchEmails(token, query, maxResults = 10, recipientFilter = null) {
  const select = '$select=id,subject,from,toRecipients,receivedDateTime,hasAttachments,bodyPreview';

  function mapMessages(msgs) {
    return (msgs || []).map(msg => ({
      id: msg.id,
      subject: msg.subject || '(no subject)',
      from: msg.from?.emailAddress?.name || msg.from?.emailAddress?.address || 'Unknown',
      fromEmail: msg.from?.emailAddress?.address || '',
      to: (msg.toRecipients || []).map(r => r.emailAddress?.address || '').filter(Boolean),
      date: msg.receivedDateTime || '',
      hasAttachments: msg.hasAttachments || false,
      preview: msg.bodyPreview || '',
    }));
  }

  // If no query and no filter — list recent emails
  if (!query && !recipientFilter) {
    const url = `${GRAPH}/me/messages?$top=${maxResults}&${select}&$orderby=receivedDateTime desc`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Email list failed (${res.status}): ${await res.text()}`);
    const data = await res.json();
    return mapMessages(data.value);
  }

  if (recipientFilter) {
    // Use $filter for recipient matching — no $orderby (not supported with lambda filters)
    const filter = `toRecipients/any(r:r/emailAddress/address eq '${recipientFilter}')`;
    const fetchCount = query ? 50 : maxResults;
    const url = `${GRAPH}/me/messages?$filter=${encodeURIComponent(filter)}&$top=${fetchCount}&${select}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Email filter failed (${res.status}): ${await res.text()}`);
    const data = await res.json();
    let results = mapMessages(data.value);

    // Client-side keyword filter when using $filter + query combo
    if (query) {
      const q = query.toLowerCase();
      results = results.filter(r =>
        r.subject.toLowerCase().includes(q) ||
        r.from.toLowerCase().includes(q) ||
        r.preview.toLowerCase().includes(q)
      );
    }

    return results.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, maxResults);
  }

  // Search by query — $search doesn't support $orderby, sort client-side
  const url = `${GRAPH}/me/messages?$search="${encodeURIComponent(query)}"&$top=${maxResults}&${select}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Email search failed (${res.status}): ${await res.text()}`);
  const data = await res.json();
  return mapMessages(data.value).sort((a, b) => new Date(b.date) - new Date(a.date));
}

export async function getEmail(token, messageId) {
  const res = await fetch(
    `${GRAPH}/me/messages/${messageId}?$select=id,subject,from,toRecipients,receivedDateTime,hasAttachments,body,bodyPreview`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Failed to get email (${res.status})`);
  const msg = await res.json();
  return {
    id: msg.id,
    subject: msg.subject || '(no subject)',
    from: msg.from?.emailAddress?.name || msg.from?.emailAddress?.address || 'Unknown',
    fromEmail: msg.from?.emailAddress?.address || '',
    to: (msg.toRecipients || []).map(r => r.emailAddress?.address || '').filter(Boolean),
    date: msg.receivedDateTime || '',
    hasAttachments: msg.hasAttachments || false,
    preview: msg.bodyPreview || '',
    bodyHtml: msg.body?.content || '',
    bodyType: msg.body?.contentType || 'text',
  };
}

export async function getEmailAttachments(token, messageId) {
  const res = await fetch(
    `${GRAPH}/me/messages/${messageId}/attachments`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Failed to get attachments (${res.status})`);
  const data = await res.json();
  return (data.value || []).map(att => ({
    id: att.id,
    name: att.name || 'attachment',
    contentType: att.contentType || 'application/octet-stream',
    size: att.size || 0,
    contentBytes: att.contentBytes || null, // base64 encoded
  }));
}

export async function saveAttachmentToVault(token, messageId, attachmentId, attachmentName, vaultFolderId) {
  // 1. Get the attachment with content
  const attRes = await fetch(
    `${GRAPH}/me/messages/${messageId}/attachments/${attachmentId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!attRes.ok) throw new Error(`Failed to download attachment (${attRes.status})`);
  const att = await attRes.json();

  if (!att.contentBytes) throw new Error('Attachment has no content');

  // 2. Convert base64 to blob
  const byteChars = atob(att.contentBytes);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteArray[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([byteArray], { type: att.contentType || 'application/octet-stream' });

  // 3. Upload to Family Vault
  const driveId = await resolveDriveId(token);
  const fileName = encodeURIComponent(attachmentName || att.name || 'attachment');
  const uploadUrl = vaultFolderId
    ? `${GRAPH}/drives/${driveId}/items/${vaultFolderId}:/${fileName}:/content`
    : `${GRAPH}/drives/${driveId}/root:/${fileName}:/content`;

  const uploadRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': att.contentType || 'application/octet-stream',
    },
    body: blob,
  });
  if (!uploadRes.ok) throw new Error(`Failed to save to Vault (${uploadRes.status}): ${await uploadRes.text()}`);
  const saved = await uploadRes.json();
  return { name: saved.name, webUrl: saved.webUrl, id: saved.id };
}

// Move an email to a mail folder by name (e.g., "Thomas Saved")
let cachedMailFolders = null;

export async function resolveMailFolderId(token, folderName) {
  if (!cachedMailFolders) {
    // Get top-level folders
    const res = await fetch(`${GRAPH}/me/mailFolders?$top=100`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to list mail folders (${res.status})`);
    const data = await res.json();
    cachedMailFolders = data.value || [];

    // Also get child folders of Inbox
    const inbox = cachedMailFolders.find(f => f.displayName === 'Inbox');
    if (inbox) {
      const childRes = await fetch(`${GRAPH}/me/mailFolders/${inbox.id}/childFolders?$top=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (childRes.ok) {
        const childData = await childRes.json();
        cachedMailFolders = [...cachedMailFolders, ...(childData.value || [])];
      }
    }
  }

  const match = cachedMailFolders.find(f =>
    f.displayName.toLowerCase() === folderName.toLowerCase()
  );
  if (!match) throw new Error(`Mail folder "${folderName}" not found`);
  return match.id;
}

export async function moveEmailToFolder(token, messageId, folderName) {
  const folderId = await resolveMailFolderId(token, folderName);
  const res = await fetch(`${GRAPH}/me/messages/${messageId}/move`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ destinationId: folderId }),
  });
  if (!res.ok) throw new Error(`Failed to move email (${res.status}): ${await res.text()}`);
  return res.json();
}

// Resolve a folder path like "Business/Insurance" to a folder ID
export async function resolveVaultFolderPath(token, folderPath) {
  if (!folderPath) return null; // root
  const driveId = await resolveDriveId(token);
  const cleanPath = folderPath.replace(/^\/+|\/+$/g, '');
  const res = await fetch(
    `${GRAPH}/drives/${driveId}/root:/${encodeURIComponent(cleanPath)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    // Try without encoding the full path (slashes need to stay)
    const res2 = await fetch(
      `${GRAPH}/drives/${driveId}/root:/${cleanPath}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!res2.ok) throw new Error(`Vault folder "${folderPath}" not found`);
    const data2 = await res2.json();
    return data2.id;
  }
  const data = await res.json();
  return data.id;
}
